/**
 * Quickstart documentation page
 */

import {DocsContent} from '../../components/docs/DocsContent';
import {CodeBlock} from '../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';
import {ROUTES} from '../../config/constants';

export function Quickstart() {
  return (
    <DocsContent
      title="Quickstart"
      description="Get up and running with VoxaPilot in minutes. Learn how to record your first meeting and generate transcripts."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-xl font-bold text-textPrimary mb-4">Installation</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot is available as a web application and desktop app. Choose the option that
            works best for you.
          </p>

          <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3 mt-6">Web Application</h3>
          <p className="text-textSecondary dark:text-[#858585] mb-4">
            Simply visit{' '}
            <Link to={ROUTES.HOME} className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
              voxapilot.com
            </Link>{' '}
            and sign up for a free account. No installation required.
          </p>

          <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3 mt-6">Desktop App</h3>
          <p className="text-textSecondary dark:text-[#858585] mb-4">
            Download the VoxaPilot desktop app for Windows, macOS, or Linux. The desktop app
            provides offline capabilities and better performance.
          </p>
          <CodeBlock
            code={`# Download for Windows
# Visit the downloads page to get the latest version

# Or use the direct download link
# https://voxapilot.com/downloads/VoxaPilot-Setup.exe`}
            language="bash"
          />
        </section>

        <section>
          <h2 className="text-xl font-bold text-textPrimary mb-4">Create Your First Meeting</h2>
          <p className="text-textSecondary mb-4">
            Once you've signed up, you can start recording meetings immediately. Here's how:
          </p>

          <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3 mt-6">Step 1: Connect a Meeting Platform</h3>
          <p className="text-textSecondary dark:text-[#858585] mb-4">
            VoxaPilot integrates with Zoom, Google Meet, Microsoft Teams, and more. Connect your
            preferred platform from the Integrations page.
          </p>

          <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3 mt-6">Step 2: Start Recording</h3>
          <p className="text-textSecondary dark:text-[#858585] mb-4">
            When you start a meeting, VoxaPilot will automatically begin transcribing. You can also
            manually start recording from the dashboard.
          </p>

          <CodeBlock
            code={`// Example: Start a meeting recording via API
const response = await fetch('https://api.voxapilot.com/v1/meetings', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    title: 'Team Standup',
    platform: 'zoom',
    meetingId: '123456789',
  }),
});

const meeting = await response.json();
console.log('Meeting started:', meeting.id);`}
            language="javascript"
          />

          <h3 className="text-lg font-semibold text-textPrimary dark:text-[#CCCCCC] mb-3 mt-6">Step 3: View Your Transcript</h3>
          <p className="text-textSecondary dark:text-[#858585] mb-4">
            After the meeting ends, your transcript will be available in the dashboard. You can
            view it, search through it, and generate AI summaries.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/concepts" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Learn about core concepts
              </Link>
            </li>
            <li>
              <Link to="/docs/features/transcription" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Explore transcription features
              </Link>
            </li>
            <li>
              <Link to="/docs/api/overview" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Check out the API documentation
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="Quickstart" />
      </div>
    </DocsContent>
  );
}

